/**
 * @description Check if the order data is valid
 * @function checkValidOrderData
 * @param {Object} to
 */
function checkValidOrderData(to) {
  const retailerStore = useCustomerReturnRetailerStore()
  const orderStore = useCustomerReturnOrderStore()
  const { retailer } = storeToRefs(retailerStore)
  const { orderNumber } = storeToRefs(orderStore)
  if (!retailer.value && !orderNumber.value && to.path !== '/app/return' && to.path !== '/app/return/confirmation') {
    return { name: 'AppReturnLookupOrder' }
  }
}

/**
 * @description Guard for item processing routes with stage/step params
 * Handles direct URL entry and page refresh scenarios
 * @function checkItemProcessingState
 * @param {Object} to
 */
function checkItemProcessingState(to) {
  const { stage } = to.params

  // If no stage param, proceed normally (initial entry to gather-return-info)
  if (!stage) {
    return
  }

  // Stage param present - verify conversation state exists
  const conversationStore = useCustomerReturnConversationStore()
  const sessionStore = useCustomerReturnSessionStore()

  // Check if we have an active item
  const itemIndex = parseInt(to.params.idx, 10)
  if (isNaN(itemIndex)) {
    return { name: 'AppReturnLookupOrder' }
  }

  // Get the item from session store
  const item = sessionStore.itemsToProcess[itemIndex]
  if (!item || !item.lineItemSessionId) {
    // No item at this index - redirect to start
    return {
      name: 'AppReturnProcessItem',
      params: { idx: to.params.idx, slug: 'gather-return-info' },
    }
  }

  // Set as active item if not already
  if (conversationStore.activeItemSessionId !== item.lineItemSessionId) {
    conversationStore.setActiveItem(item.lineItemSessionId)
  }

  const hasHist = conversationStore.hasHistory()
  // Check if conversation history exists
  if (!hasHist) {
    // No history - this is likely a direct URL entry or refresh with stale state
    // Redirect to start of this item's flow
    return {
      name: 'AppReturnProcessItem',
      params: { idx: to.params.idx, slug: 'gather-return-info' },
    }
  }

  // All checks passed - proceed to the route
}

/**
 * @description PEG-786 Source 3: guard the summary against direct entry that
 * would bypass the dynamic-question injection. The injection normally runs in
 * `moveToRoute('summary')`, but a shopper landing on `/app/return/summary`
 * directly (URL edit, browser history) would skip it. Load the questions
 * (idempotent backend GET) and redirect to the dynamic-questions step when any
 * answer is unpersisted or a blocking answer was already recorded. Fails safe:
 * `loadQuestions()` returns empty on error, so a load failure never strands the
 * shopper out of the summary.
 * @function checkDynamicQuestionsResolved
 */
async function checkDynamicQuestionsResolved() {
  // PEG-968 (#1): once the return is submitted, never bounce back to questions.
  // completeSubmission writes the return even if the submit-path question load
  // failed transiently; the confirmation beforeEnter then re-reads questions and,
  // on a now-succeeding read, would redirect the customer back to the questions
  // step AFTER the return already exists. If submission is done, the questions
  // are moot — proceed.
  const submissionStore = useCustomerReturnSubmissionStore()
  if (submissionStore.isSubmitted) return

  const dynamicQuestionsStore = useCustomerReturnDynamicQuestionsStore()
  await dynamicQuestionsStore.loadQuestions()
  if (dynamicQuestionsStore.hasUnpersistedQuestions || dynamicQuestionsStore.blocked) {
    return { name: 'AppReturnDynamicQuestions' }
  }
}

/**
 * @description Check if the user can enter the return process
 * @function checkEntrance
 * @param {Object} to
 */
async function checkEntrance(to) {
  const skipReturnTutorial = localStorage.getItem('peg_skip_how_it_works') === '1'

  // IMPORTANT: We must specify query and hash explicitly to ensure Vue router handles the navigation correctly
  // Spreading `to` was causing endless redirects in some cases
  if (!skipReturnTutorial) {
    return {
      name: 'AppReturnHowItWorks',
      query: to.query,
      hash: to.hash,
    }
  }
  return {
    name: 'AppReturnLookupOrder',
    query: to.query,
    hash: to.hash,
  }
}

/**
 * /app/return routes
 * @type {Array}
 * @property {String} path
 * @property {Function} component
 * @property {Function} [beforeEnter]
 * @property {Object} [meta]
 * @property {Object} [meta.layout]
 * @property {Boolean} [meta.layout.showActionButtons]
 * @property {Boolean} [meta.layout.noContentPadding]
 * @property {Boolean} [meta.layout.showCurrentItem]
 * @property {Boolean} [meta.layout.showBottomNav]
 * @property {Object} [meta.layout.header]
 * @property {Boolean} [meta.layout.header.showSlogan]
 * @property {Boolean} [meta.layout.header.showBackButton]
 * @property {Boolean} [meta.layout.header.showOrderNumber]
 * @property {Boolean} [meta.layout.header.showUserGreeting]
 *
 */

const GLOBAL_SHOW_SLOGAN = false // Set to true to show slogan in header across all return pages (overrides individual page settings)

export default {
  path: '/app/return',
  component: () => import('src/layouts/app/AppReturnLayout.vue'),
  meta: {
    layout: {
      header: {
        showSlogan: GLOBAL_SHOW_SLOGAN,
      },
    },
  },
  children: [
    {
      path: '',
      name: 'AppReturnHome',
      beforeEnter: checkEntrance,
      component: () => import('src/pages/app/return/AppReturnPage.vue'),
    },
    // order lookup
    {
      path: 'lookup-order',
      name: 'AppReturnLookupOrder',
      component: () => import('src/pages/app/return/AppReturnLookupOrder.vue'),
      meta: {
        layout: {
          showBottomNav: true,
          header: {
            showSlogan: GLOBAL_SHOW_SLOGAN,
          },
        },
      },
    },
    // gift order lookup
    {
      path: 'lookup-gift',
      name: 'AppReturnLookupGift',
      component: () => import('src/pages/app/return/AppReturnLookupGift.vue'),
      meta: {
        layout: {
          showBottomNav: true,
          header: {
            showSlogan: GLOBAL_SHOW_SLOGAN,
          },
        },
      },
    },
    // contentful
    {
      path: 'how-to-start',
      name: 'AppReturnHowToStart',
      component: () => import('src/pages/app/return/AppReturnHowToStartReturn.vue'),
      meta: {
        layout: {
          showBottomNav: true,
          contentful: true,
        },
      },
    },
    {
      path: 'how-it-works',
      name: 'AppReturnHowItWorks',
      component: () => import('src/pages/app/return/AppReturnHowItWorks.vue'),
      meta: {
        layout: {
          showBottomNav: true,
          contentful: true,
        },
      },
    },
    {
      path: 'what-is-peg',
      name: 'AppReturnWhatIsPeg',
      component: () => import('src/pages/app/return/AppReturnWhatIsPeg.vue'),
      meta: {
        layout: {
          showBottomNav: true,
          contentful: true,
        },
      },
    },
    // retailer selection
    {
      path: 'select-retailer',
      name: 'AppReturnSelectRetailer',
      component: () => import('src/pages/app/return/AppReturnSelectRetailer.vue'),
      beforeEnter: checkValidOrderData,
      meta: {
        layout: {
          showActionButtons: true,
          header: {
            showSlogan: GLOBAL_SHOW_SLOGAN,
          },
        },
      },
    },
    // choose items
    {
      path: 'items',
      name: 'AppReturnChooseItems',
      component: () => import('src/pages/app/return/AppReturnChooseItems.vue'),
      beforeEnter: checkValidOrderData,
      meta: {
        layout: {
          showActionButtons: true,
          header: {
            showBackButton: true,
            showOrderNumber: false,
            showUserGreeting: true,
          },
        },
      },
    },
    // processing an item
    {
      path: 'item/:idx/:slug/:stage?/:step?',
      name: 'AppReturnProcessItem',
      component: () => import('pages/app/return/AppReturnProcessItem.vue'),
      beforeEnter: [checkValidOrderData, checkItemProcessingState],
      meta: {
        confirmExit: true,
        layout: {
          showActionButtons: true,
          showCurrentItem: true,
          header: {
            showBackButton: true,
            showOrderNumber: true,
          },
        },
      },
    },
    // dynamic questions (PEG-786 Source 3) — AI-generated questions injected
    // after all items are processed and before the summary, for non-returnable
    // statements that can't be resolved deterministically.
    {
      path: 'dynamic-questions',
      name: 'AppReturnDynamicQuestions',
      component: () => import('src/pages/app/return/AppReturnDynamicQuestions.vue'),
      beforeEnter: checkValidOrderData,
      meta: {
        confirmExit: true,
        layout: {
          showActionButtons: false,
          header: {
            showOrderNumber: true,
          },
        },
      },
    },
    // processing item summary
    {
      path: 'summary',
      name: 'AppReturnSummary',
      component: () => import('src/pages/app/return/AppReturnSummary.vue'),
      beforeEnter: [checkValidOrderData, checkDynamicQuestionsResolved],
      meta: {
        confirmExit: true,
        layout: {
          showActionButtons: true,
          header: {
            showOrderNumber: true,
          },
        },
      },
    },
    // processing item confirmation page
    {
      path: 'confirmation',
      name: 'AppReturnConfirmation',
      component: () => import('src/pages/app/return/AppReturnConfirmation.vue'),
      // Defense in depth (PEG-786 / DEV-0407): the primary gate is in the submit
      // action (AppReturnActions.onSubmitReturn) BEFORE the backend write; this
      // guard is a secondary net so an unresolved-dynamic-questions session can't
      // even land on the confirmation page. Fails safe (loadQuestions empty on
      // error -> no redirect).
      beforeEnter: checkDynamicQuestionsResolved,
      meta: {
        layout: {
          showBottomNav: true,
          noContentPadding: true,
          header: {
            showSlogan: GLOBAL_SHOW_SLOGAN,
          },
        },
      },
    },
  ],
}
